import jwtDecode from 'jwt-decode';
// import axios from 'src/utils/axios';
import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isAuthenticated: false,
  user: { displayName: '', message: '' },
  // MFA flow
  mfaPending: false,
  mfaToken: null,
  mfaMetodo: null,
  setupRequired: false,
  // MFA user status (populated on initialize)
  mfaConfigurado: false,
  mfaObligatorio: false,
  mfaActivo: false,
};

const slice = createSlice({
  name: 'authJwt',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // INITIALISE
    getInitialize(state, action) {
      state.isLoading = false;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user;
    },

    // LOGIN
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      // clear MFA state on successful login
      state.mfaPending = false;
      state.mfaToken = null;
      state.mfaMetodo = null;
      state.setupRequired = false;
    },

    // UPDATE MFA USER STATUS (after setup/reset)
    updateMfaStatus(state, action) {
      if (action.payload.mfaConfigurado !== undefined) state.mfaConfigurado = action.payload.mfaConfigurado;
      if (action.payload.mfaObligatorio !== undefined) state.mfaObligatorio = action.payload.mfaObligatorio;
      if (action.payload.mfaActivo !== undefined) state.mfaActivo = action.payload.mfaActivo;
    },

    // MFA PENDING — login requires MFA step
    mfaPendingSuccess(state, action) {
      state.mfaPending = true;
      state.mfaToken = action.payload.mfaToken;
      state.mfaMetodo = action.payload.mfaMetodo;
      state.setupRequired = action.payload.setupRequired;
    },

    // REGISTER
    registerSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },

    // LOGOUT
    logoutSuccess(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.mfaPending = false;
      state.mfaToken = null;
      state.mfaMetodo = null;
      state.setupRequired = false;
      state.mfaConfigurado = false;
      state.mfaObligatorio = false;
      state.mfaActivo = false;
    },

    // UPDATE ACCOUNT
    updateProfile(state, action) {
      state.user = { ...state.user, ...action.payload };
    },
  },
});

// Reducer
export default slice.reducer;

// Export updateMfaStatus action for use in components
export const { updateMfaStatus } = slice.actions;

// ----------------------------------------------------------------------

const isValidToken = accessToken => {
  if (!accessToken) {
    return false;
  }
  const decoded = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

const setSession = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

// ----------------------------------------------------------------------

export function login({ email, password }) {
  return async dispatch => {
    try {
      const response = await axios.post('/api/token/', {
        username: email,
        password,
      });

      const data = response.data;

      // MFA requerido — el backend devuelve mfa_required en lugar de tokens
      if (data.mfa_required) {
        dispatch(slice.actions.mfaPendingSuccess({
          mfaToken: data.mfa_token,
          mfaMetodo: data.mfa_metodo || 'totp',
          setupRequired: data.setup_required || false,
        }));
        return {
          mfaRequired: true,
          setupRequired: data.setup_required || false,
        };
      }

      const { refresh, access } = data;
      setSession(access, refresh);
      dispatch(slice.actions.loginSuccess({ user: { email } }));
    } catch (error) {
      const status = error?.response?.status;           // 401, 429 o undefined
      const detail = error?.response?.data?.detail;    // mensaje en español del backend

      const enrichedError = new Error(detail || 'Error al iniciar sesión');
      enrichedError.status = status || 0;              // 0 = error de red / CORS / timeout
      throw enrichedError;
    }
  };
}

// ----------------------------------------------------------------------

export function verifyMfa({ mfa_token, otp }) {
  return async dispatch => {
    const response = await axios.post('/api/token/mfa-verify/', { mfa_token, otp });
    const { access, refresh } = response.data;
    setSession(access, refresh);
    dispatch(slice.actions.loginSuccess({ user: {} }));
  };
}

// ----------------------------------------------------------------------

export function confirmMfaSetup({ mfa_token, otp }) {
  return async dispatch => {
    const response = await axios.post('/api/token/mfa-setup-verify/', { mfa_token, otp });
    const { access, refresh } = response.data;
    setSession(access, refresh);
    dispatch(slice.actions.loginSuccess({ user: {} }));
  };
}

// ----------------------------------------------------------------------

export function getMfaSetup(mfa_token) {
  return async () => {
    const response = await axios.post('/api/token/mfa-setup/', { mfa_token });
    return response.data;
  };
}

// ----------------------------------------------------------------------

export function register({ email, password, firstName, lastName }) {
  return async dispatch => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { access, user } = response.data;

    window.localStorage.setItem('accessToken', access);
    dispatch(slice.actions.registerSuccess({ user }));
  };
}

// ----------------------------------------------------------------------

export function logout() {
  return async dispatch => {
    setSession(null);
    dispatch(slice.actions.logoutSuccess());
  };
}

// ----------------------------------------------------------------------

export function getInitialize() {
  return async dispatch => {
    dispatch(slice.actions.startLoading());
    try {
      const accessToken = window.localStorage.getItem('accessToken');

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const response = await axios.post('/api/account');

        const {
          data: { user, polysignkey },
        } = response.data;

        // Fetch MFA org/user status
        let mfaConfigurado = false;
        let mfaObligatorio = false;
        let mfaActivo = false;
        try {
          const mfaRes = await axios.get('/api/mfa/config/');
          const mfaData = mfaRes.data?.data || {};
          mfaConfigurado = mfaData.mfa_configurado || false;
          mfaObligatorio = mfaData.mfa_obligatorio || false;
          mfaActivo = mfaData.mfa_activo || false;
        } catch (_) {}

        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: true,
            user: { displayName: user?.first_name, ...user, polysignkey },
          }),
        );
        dispatch(slice.actions.updateMfaStatus({ mfaConfigurado, mfaObligatorio, mfaActivo }));
      } else {
        dispatch(
          slice.actions.getInitialize({
            isAuthenticated: false,
            user: null,
          }),
        );
      }
    } catch (error) {
      dispatch(
        slice.actions.getInitialize({
          isAuthenticated: false,
          user: null,
        }),
      );
    }
  };
}

// ********************************

export function updateProfile({ first_name, last_name }) {
  return async dispatch => {
    const response = await axios.post('/api/account/update', {
      first_name,
      last_name,
    });

    const { message } = response.data;

    dispatch(
      slice.actions.updateProfile({ first_name, last_name, message, displayName: first_name }),
    );
  };
}
